import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Feedback } from "@/types/feedback";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import { Eye } from "lucide-react";
import { FeedbackDetail } from "./FeedbackDetail";

const statusLabels: Record<string, string> = {
  new: "Novo",
  in_progress: "Em Andamento",
  resolved: "Resolvido",
  ignored: "Ignorado",
};

const typeLabels: Record<string, string> = {
  bug: "Erro",
  suggestion: "Sugestão",
};

export function FeedbackList() {
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);

  const fetchFeedbacks = async () => {
    setLoading(true);
    let query = supabase
      .from("feedbacks")
      .select("*")
      .order("created_at", { ascending: false });

    if (statusFilter !== "all") {
      query = query.eq("status", statusFilter);
    }
    if (typeFilter !== "all") {
      query = query.eq("type", typeFilter);
    }

    const { data, error } = await query;
    if (error) {
      console.error("Erro ao buscar feedbacks:", error);
    } else {
      setFeedbacks(data as Feedback[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchFeedbacks();
  }, [statusFilter, typeFilter]);

  const handleStatusChange = async (id: string, newStatus: string) => {
    const { error } = await supabase
      .from("feedbacks")
      .update({ status: newStatus })
      .eq("id", id);

    if (error) {
      console.error("Erro ao atualizar status:", error);
    } else {
      fetchFeedbacks();
      if (selectedFeedback && selectedFeedback.id === id) {
          setSelectedFeedback({ ...selectedFeedback, status: newStatus as any });
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os Status</SelectItem>
            <SelectItem value="new">Novo</SelectItem>
            <SelectItem value="in_progress">Em Andamento</SelectItem>
            <SelectItem value="resolved">Resolvido</SelectItem>
            <SelectItem value="ignored">Ignorado</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os Tipos</SelectItem>
            <SelectItem value="bug">Erro</SelectItem>
            <SelectItem value="suggestion">Sugestão</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Mensagem</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center">
                  Carregando...
                </TableCell>
              </TableRow>
            ) : feedbacks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center">
                  Nenhum feedback encontrado.
                </TableCell>
              </TableRow>
            ) : (
              feedbacks.map((feedback) => (
                <TableRow key={feedback.id}>
                  <TableCell>
                    {format(new Date(feedback.created_at), "dd/MM/yyyy HH:mm")}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={feedback.type === "bug" ? "destructive" : "default"}
                    >
                      {typeLabels[feedback.type] || feedback.type}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-[300px] truncate">
                    {feedback.message}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {statusLabels[feedback.status] || feedback.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedFeedback(feedback)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <FeedbackDetail
        feedback={selectedFeedback}
        onClose={() => setSelectedFeedback(null)}
        onStatusChange={handleStatusChange}
      />
    </div>
  );
}
